import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateAndTime } from '@adflow/shared/util-formatting';
import type { HistoryGMBTemplateDto } from '@adflow/shared/util-history';
import { isNotNullish } from '@adflow/shared/util-js';

import { HistoryEntryFieldArrayItems } from './fields/HistoryEntryFieldArrayItems';
import { HistoryEntryFieldImages } from './fields/HistoryEntryFieldImages';
import { HistoryEntryLayout } from './HistoryEntryLayout';
import { useFormatHistoryValueDefault } from './util/useFormatHistoryValueDefault';

interface HistoryGMBTemplateProps {
	data: {
		oldData: null | HistoryGMBTemplateDto;
		newData: HistoryGMBTemplateDto;
	};
}

export function HistoryGMBTemplate({ data }: HistoryGMBTemplateProps) {
	const { t } = useTranslation(['common', 'facebook', 'google-ads']);

	const formatHistoryValueDefault = useFormatHistoryValueDefault();

	const formatTemplateValue = useCallback(
		(_data: HistoryGMBTemplateDto, key: keyof HistoryGMBTemplateDto) => {
			switch (key) {
				case 'languageCode': {
					return t(`common:language-${_data[key]}`);
				}
				case 'startDate': {
					return _data[key] ? formatDateAndTime(_data[key]) : _data[key];
				}
				case 'endDate': {
					return _data[key] ? formatDateAndTime(_data[key]) : _data[key];
				}
				case 'scheduledPublishAt': {
					return _data[key] ? formatDateAndTime(_data[key]) : _data[key];
				}
				default: {
					return formatHistoryValueDefault(_data[key]);
				}
			}
		},
		[formatHistoryValueDefault, t]
	);

	const imagesData = {
		oldData: data.oldData?.image ? [data.oldData?.image] : [],
		newData: data.newData?.image ? [data.newData?.image] : [],
	};

	return (
		<HistoryEntryLayout
			formatValue={formatTemplateValue}
			data={data}
			skipKeys={[
				'locations',
				'id',
				'createdById',
				'archived',
				'createdAt',
				'image',
				'posts',
			]}
		>
			<HistoryEntryFieldArrayItems
				oldArrayItems={
					data.oldData?.locations?.filter(isNotNullish)?.map((l) => ({
						name: l?.name ?? l.googleId,
						...l,
					})) ?? []
				}
				newArrayItems={
					data.newData?.locations?.filter(isNotNullish)?.map((l) => ({
						name: l?.name ?? l.googleId,
						...l,
					})) ?? []
				}
				label="Locations"
			/>
			<HistoryEntryFieldImages
				oldImages={imagesData.oldData}
				newImages={imagesData.newData}
				label="Image"
			/>
		</HistoryEntryLayout>
	);
}
